import { useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { RunRecommendedRules } from '@/features/rules';
import { SCREEN_PATHS } from '@/shared/routes';
import { Button } from '@/shared/ui/Button';

import { useBindRecommended } from '../api/rulegen';
import type {
  AutoSuggestPreview,
  AutoSuggestRecommendation,
  AutoSuggestResult,
} from '../api/rulegen';
import {
  allSelected,
  boundBindingIds,
  draftLink,
  initialSelection,
  resultTone,
  ruleNameOf,
  sectionsOf,
  toggled,
} from '../model/autoSuggest';
import { coverageByDimension, itemsFromRecommendations } from '../model/coverage';
import { CoverageReadout } from './CoverageReadout';

/* Auto Suggest on a dataset's Rules tab: recommend, select, bind, run.
 *
 * **Nothing happens without a person choosing it.** The button only recommends. A person ticks the
 * rules to add — strong evidence arrives ticked, a guess does not — and Bind adds exactly those, each
 * to a rule two people already approved. Running them is a further, separate press. A recommendation
 * no approved rule serves is never drafted here; it links to the Suggestions screen, where the
 * existing "Create a new rule" path drafts it and two others approve it.
 */

export function AutoSuggestButton({
  busy,
  onRun,
}: {
  readonly busy: boolean;
  readonly onRun: () => void;
}): ReactElement {
  const { t } = useTranslation();
  return (
    <Button
      tone="secondary"
      className="lq-autosg__btn"
      busy={busy}
      title={t('dataset.rules.auto.meaning')}
      onClick={onRun}
    >
      {busy ? null : (
        <span className="lq-autosg__glyph" aria-hidden="true">
          ✦
        </span>
      )}
      {busy ? t('dataset.rules.auto.busy') : t('dataset.rules.auto.button')}
    </Button>
  );
}

export function AutoSuggestPanel({
  datasetId,
  connectionId,
  preview,
  loading,
  error,
  onDismiss,
}: {
  readonly datasetId: string;
  /** The dataset's source, which the Suggestions screen's link needs. Absent while it loads. */
  readonly connectionId: string | undefined;
  readonly preview: AutoSuggestPreview | undefined;
  readonly loading: boolean;
  readonly error: Error | null;
  readonly onDismiss: () => void;
}): ReactElement | null {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="lq-autosg" role="status">
        <p className="lq-autosg__note">{t('dataset.rules.auto.loading')}</p>
        <span className="lq-skeleton__bar lq-drp__loading" aria-hidden="true" />
      </div>
    );
  }
  if (error !== null) {
    /* The whole request rolled back, so nothing it started is half-kept — the sentence says so,
       which is what makes "try again" safe advice. */
    return (
      <p className="lq-drp__runError" role="alert">
        {t('dataset.rules.auto.refused', { reason: error.message })}
      </p>
    );
  }
  if (preview === undefined) return null;

  return (
    <RecommendedList
      /* A new recommendation set (every regeneration mints new ids) starts a fresh selection, so a
         tick from the previous list can never select a row the reader has not seen. */
      key={preview.recommendations.map((item) => item.suggestion_id).join(',')}
      datasetId={datasetId}
      connectionId={connectionId}
      preview={preview}
      onDismiss={onDismiss}
    />
  );
}

/** The four steps, each a person's: recommend, select, add, run. Shown so "Add" is visibly not
 * "Run", which is the distinction a reader most often collapses — adding a rule reads nothing from
 * the source, and running it does. */
const STEPS = ['recommend', 'select', 'add', 'run'] as const;

function Steps({ current }: { readonly current: number }): ReactElement {
  const { t } = useTranslation();
  return (
    <ol className="lq-autosg__steps" aria-label={t('dataset.rules.auto.steps.label')}>
      {STEPS.map((step, index) => (
        <li
          key={step}
          className={
            index < current
              ? 'lq-autosg__step lq-autosg__step--done'
              : index === current
                ? 'lq-autosg__step lq-autosg__step--current'
                : 'lq-autosg__step'
          }
          aria-current={index === current ? 'step' : undefined}
        >
          <span className="lq-autosg__stepNum" aria-hidden="true">
            {index < current ? '✓' : String(index + 1)}
          </span>
          {t(`dataset.rules.auto.steps.${step}`)}
        </li>
      ))}
    </ol>
  );
}

function RecommendedList({
  datasetId,
  connectionId,
  preview,
  onDismiss,
}: {
  readonly datasetId: string;
  readonly connectionId: string | undefined;
  readonly preview: AutoSuggestPreview;
  readonly onDismiss: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const sections = sectionsOf(preview);
  const [selection, setSelection] = useState(() => initialSelection(preview));
  const bind = useBindRecommended();
  const everything = allSelected(sections.bindable, selection);
  const chosen = sections.bindable.filter((item) => selection.has(item.suggestion_id));

  const head = (
    <>
      <div className="lq-autosg__head">
        <span className="lq-autosg__title">
          <strong>{t('dataset.rules.auto.title')}</strong>{' '}
          {t('dataset.rules.auto.sources', {
            lumiq: preview.recommendations.filter((item) => item.origin !== 'ai').length,
            ai: preview.recommendations.filter((item) => item.origin !== 'system').length,
          })}
        </span>
        <button type="button" className="lq-autosg__toggle" onClick={onDismiss}>
          {t('dataset.rules.auto.dismiss')}
        </button>
      </div>
      <Steps current={bind.data === undefined ? 1 : 3} />
    </>
  );

  if (bind.data !== undefined) {
    return (
      <section className="lq-autosg" aria-label={t('dataset.rules.auto.title')}>
        {head}
        <BindOutcome result={bind.data} datasetId={datasetId} />
      </section>
    );
  }

  return (
    <section className="lq-autosg" aria-label={t('dataset.rules.auto.title')}>
      {head}
      {preview.generation.profile_status === 'never_profiled' ? (
        <p className="lq-autosg__note">
          {t('dataset.rules.auto.profile.never_profiled')}
        </p>
      ) : null}

      {preview.recommendations.length === 0 ? (
        <p className="lq-autosg__note">{t('dataset.rules.auto.none')}</p>
      ) : null}

      <CoverageReadout
        compact
        readout={coverageByDimension(
          itemsFromRecommendations(preview.recommendations),
          preview.generation.coverage_gaps ?? null,
        )}
      />

      {sections.bindable.length > 0 ? (
        <>
          <h3 className="lq-autosg__groupTitle">
            {t('dataset.rules.auto.section.bindable', {
              count: sections.bindable.length,
            })}
          </h3>
          <div className="lq-drp__scroll">
            <table className="lq-drp__table">
              <thead>
                <tr>
                  <th scope="col" className="lq-autosg__checkCell">
                    <input
                      type="checkbox"
                      aria-label={t('dataset.rules.auto.selectAll')}
                      checked={everything}
                      disabled={bind.isPending}
                      onChange={() => {
                        setSelection(
                          everything
                            ? new Set()
                            : new Set(
                                sections.bindable.map((item) => item.suggestion_id),
                              ),
                        );
                      }}
                    />
                  </th>
                  <th scope="col">{t('dataset.rules.auto.column.rule')}</th>
                  <th scope="col">{t('dataset.rules.auto.column.category')}</th>
                  <th scope="col">{t('dataset.rules.auto.column.columns')}</th>
                  <th scope="col">{t('dataset.rules.auto.column.description')}</th>
                  <th scope="col">{t('dataset.rules.auto.column.source')}</th>
                </tr>
              </thead>
              <tbody>
                {sections.bindable.map((item) => (
                  <RecommendationLine
                    key={item.suggestion_id}
                    item={item}
                    checked={selection.has(item.suggestion_id)}
                    disabled={bind.isPending}
                    onToggle={() => {
                      setSelection((current) => toggled(current, item.suggestion_id));
                    }}
                  />
                ))}
              </tbody>
            </table>
          </div>
          <div className="lq-autosg__actions">
            <Button
              tone="primary"
              busy={bind.isPending}
              disabled={chosen.length === 0}
              onClick={() => {
                bind.mutate({
                  datasetId,
                  suggestionIds: chosen.map((item) => item.suggestion_id),
                });
              }}
            >
              {t('dataset.rules.auto.bind', { count: chosen.length })}
            </Button>
            <span className="lq-autosg__note">{t('dataset.rules.auto.bindNote')}</span>
          </div>
          {bind.isError ? (
            <p className="lq-autosg__error" role="alert">
              {t('dataset.rules.auto.bindRefused', { reason: bind.error.message })}
            </p>
          ) : null}
        </>
      ) : null}

      {sections.needsNewRule.length > 0 ? (
        <>
          <h3 className="lq-autosg__groupTitle">
            {t('dataset.rules.auto.section.newRule', {
              count: sections.needsNewRule.length,
            })}
          </h3>
          <p className="lq-autosg__note">{t('dataset.rules.auto.newRuleNote')}</p>
          <ul className="lq-autosg__list">
            {sections.needsNewRule.map((item) => (
              <li key={item.suggestion_id} className="lq-autosg__item">
                <span>
                  {ruleNameOf(item)}
                  <span className="lq-autosg__by">
                    {t(`dataset.rules.auto.origin.${item.origin}`)}
                  </span>
                </span>
                <span className="lq-autosg__why">{item.description}</span>
                {item.needs_input.length > 0 ? (
                  <span className="lq-autosg__why">
                    <span className="lq-sg__tag lq-sg__tag--template">
                      {t('suggestions.row.template')}
                    </span>{' '}
                    {t('suggestions.row.templateHint', {
                      fields: item.needs_input.join(', '),
                    })}
                  </span>
                ) : null}
                <Link
                  className="lq-autosg__link"
                  to={
                    connectionId === undefined
                      ? SCREEN_PATHS.suggestions
                      : draftLink({
                          connectionId,
                          datasetId,
                          suggestionId: item.suggestion_id,
                        })
                  }
                >
                  {t('dataset.rules.auto.draft')}
                </Link>
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {sections.awaitingApproval.length > 0 ? (
        <>
          <h3 className="lq-autosg__groupTitle">
            {t('dataset.rules.auto.section.awaiting', {
              count: sections.awaitingApproval.length,
            })}
          </h3>
          <ul className="lq-autosg__list">
            {sections.awaitingApproval.map((item) => (
              <li key={item.suggestion_id} className="lq-autosg__item">
                <span>{ruleNameOf(item)}</span>
                <span className="lq-autosg__why">{item.note}</span>
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {sections.alreadyBound.length > 0 || preview.dismissed > 0 ? (
        <p className="lq-autosg__note">
          {[
            sections.alreadyBound.length > 0
              ? t('dataset.rules.auto.inPlace', { count: sections.alreadyBound.length })
              : null,
            preview.dismissed > 0
              ? t('dataset.rules.auto.dismissed', { count: preview.dismissed })
              : null,
          ]
            .filter((part) => part !== null)
            .join(' · ')}
        </p>
      ) : null}
    </section>
  );
}

function RecommendationLine({
  item,
  checked,
  disabled,
  onToggle,
}: {
  readonly item: AutoSuggestRecommendation;
  readonly checked: boolean;
  readonly disabled: boolean;
  readonly onToggle: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const name = ruleNameOf(item);
  return (
    <tr>
      <td className="lq-autosg__checkCell">
        <input
          type="checkbox"
          aria-label={t('dataset.rules.auto.select', { rule: name })}
          checked={checked}
          disabled={disabled}
          onChange={onToggle}
        />
      </td>
      <td>
        <div className="lq-drp__rule">{name}</div>
        {item.note === null ? null : <div className="lq-autosg__why">{item.note}</div>}
      </td>
      <td className="lq-drp__dim">{t(`suggestions.filter.category.${item.category}`)}</td>
      <td className="lq-drp__cols">
        {item.columns.length === 0
          ? t('dataset.rules.auto.wholeTable')
          : item.columns.join(', ')}
      </td>
      <td className="lq-autosg__why">{item.description}</td>
      <td className="lq-drp__dim">{t(`dataset.rules.auto.origin.${item.origin}`)}</td>
    </tr>
  );
}

function BindOutcome({
  result,
  datasetId,
}: {
  readonly result: AutoSuggestResult;
  readonly datasetId: string;
}): ReactElement {
  const { t } = useTranslation();
  const { summary } = result;
  const unbound = result.results.filter(
    (item) => item.outcome === 'not_bindable' || item.outcome === 'failed',
  );
  const parts = (['bound', 'already_bound', 'not_bindable', 'failed'] as const)
    .filter((outcome) => summary[outcome] > 0)
    .map((outcome) =>
      t(`dataset.rules.auto.outcome.${outcome}`, { count: summary[outcome] }),
    );
  return (
    <div className={`lq-tintband lq-tintband--${resultTone(result)} lq-autosg__result`}>
      <p className="lq-tintband__text" role="status">
        {parts.join(' · ')}
      </p>
      {unbound.length > 0 ? (
        <ul className="lq-autosg__list">
          {unbound.map((item) => (
            <li key={item.suggestion_id} className="lq-autosg__item">
              <span>
                {item.kind === null
                  ? t('dataset.rules.auto.gone')
                  : t('dataset.rules.auto.item', {
                      kind: t(`rules.kind.${item.kind}`),
                      columns:
                        item.columns.length === 0
                          ? t('dataset.rules.auto.wholeTable')
                          : item.columns.join(', '),
                    })}
              </span>
              <span className="lq-autosg__why">{item.detail}</span>
            </li>
          ))}
        </ul>
      ) : null}
      <RunRecommendedRules bindingIds={boundBindingIds(result)} datasetId={datasetId} />
    </div>
  );
}
